import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, MessageCircle, ClipboardList, FileText, CheckCircle2, Clapperboard } from "lucide-react";
import Reveal from "@/components/Reveal";
import CraftFilm from "@/components/CraftFilm";
import { serviceSentence } from "@/lib/conversation";

const STEPS = [
  { n: "01", title: "Share Your Idea", Icon: MessageCircle },
  { n: "02", title: "Your Producer Plans Everything", Icon: ClipboardList },
  { n: "03", title: "Get Your Blueprint", Icon: FileText },
  { n: "04", title: "Approve & Request Quote", Icon: CheckCircle2, accent: true },
  { n: "05", title: "We Produce. You Relax.", Icon: Clapperboard },
];

// Services grouped by outcome, not industry. People buy outcomes.
const SERVICE_GROUPS = [
  { label: "Brand", items: ["Brand Campaigns", "Brand Films", "Advertising", "Fashion"] },
  { label: "Storytelling", items: ["Documentary", "Music Videos", "Corporate Films", "Testimonials"] },
  { label: "Products", items: ["Product Films", "Product Shoots", "Real Estate"] },
  { label: "Experiences", items: ["Events", "Offline Campaigns"] },
  { label: "Culture", items: ["Social Media Content", "Influencer Campaigns"] },
  { label: "Emerging Media", items: ["AI Films"] },
];

const ProcessTimeline = () => (
  <section className="border-t border-bnm" data-testid="home-how">
    <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-12">
        <Reveal as="h2" className="type-h2">
          From idea to <span className="font-serif-italic font-light">reality</span>.
        </Reveal>
        <Reveal delay={120}>
          <Link
            to="/how-it-works"
            className="text-[14px] text-muted-ink hover:text-ink transition-colors inline-flex items-center gap-2"
            data-testid="hiw-readmore"
          >
            Full process <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>

      <Reveal>
        <ol className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-2 list-none" data-testid="home-process-timeline">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.n}>
              <li
                className="flex sm:flex-col items-center gap-4 sm:gap-0 sm:flex-1 sm:text-center"
                data-testid={`home-step-${s.n}`}
              >
                <div
                  className={`w-12 h-12 md:w-14 md:h-14 rounded-full border bg-white flex items-center justify-center shrink-0 transition-colors ${
                    s.accent
                      ? "border-[rgba(229,57,53,0.4)] text-[color:var(--bnm-red)]"
                      : "border-bnm text-ink"
                  }`}
                >
                  <s.Icon className="w-5 h-5 md:w-[22px] md:h-[22px]" strokeWidth={1.5} aria-hidden />
                </div>
                <div className="sm:mt-4 text-left sm:text-center sm:px-2">
                  <div className="text-[14px] md:text-[14.5px] font-semibold text-ink leading-[1.35] tracking-[-0.01em]">
                    {i + 1}. {s.title}
                  </div>
                </div>
              </li>
              {i < STEPS.length - 1 && (
                <li aria-hidden className="hidden sm:flex items-start justify-center pt-4 md:pt-5 text-muted-ink/40 shrink-0">
                  <ArrowRight className="w-4 h-4" />
                </li>
              )}
            </React.Fragment>
          ))}
        </ol>
      </Reveal>
    </div>
  </section>
);

const Philosophy = () => (
  <section className="border-t border-bnm" data-testid="home-philosophy">
    <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
      <Reveal as="h2" className="type-h2">
        Every idea deserves
        <br />
        a <span className="font-serif-italic font-light bnm-denim-shine">Producer</span>.
      </Reveal>
      <Reveal delay={120}>
        <p className="mt-7 md:mt-8 text-ink text-[17px] md:text-[19px] leading-[2.1] tracking-[-0.005em]">
          Ideas become brands.
          <br />
          Brands become stories.
          <br />
          Stories become culture.
          <br />
          Culture creates legacy.
        </p>
      </Reveal>
      <Reveal delay={200}>
        <p className="mt-6 md:mt-7 text-muted-ink text-[15.5px] md:text-[16.5px] leading-[1.75] max-w-[520px] mx-auto">
          Our role is making sure nothing gets lost between imagination and execution.
        </p>
      </Reveal>
    </div>
  </section>
);

const ServicesRows = ({ startConversation }) => {
  const [openServices, setOpenServices] = useState(() => new Set());

  return (
    <section className="border-t border-bnm bg-[#fafafa]">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
          <Reveal as="h2" className="type-h2">
            Every <span className="font-serif-italic font-light">story</span>.
            <br />
            Every format.
          </Reveal>
          <Reveal delay={120}>
            <Link
              to="/services"
              className="text-[14px] text-muted-ink hover:text-ink transition-colors inline-flex items-center gap-2"
              data-testid="services-readmore"
            >
              All services <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
        <div className="flex flex-col" data-testid="home-services-rows">
          {SERVICE_GROUPS.map((group, gi) => {
            const slug = group.label.toLowerCase().replace(/\s+/g, "-");
            const isOpen = openServices.has(slug);
            return (
              <Reveal key={group.label} delay={gi * 60} data-testid={`service-group-${slug}`}>
                <div className={`flex flex-col md:flex-row md:items-baseline md:gap-10 py-4 md:py-5 ${gi > 0 ? "border-t border-bnm" : ""}`}>
                  <div className="md:w-[170px] shrink-0">
                    {/* Desktop label: starts a conversation. */}
                    <button
                      type="button"
                      onClick={() => startConversation()}
                      className="hidden md:inline eyebrow eyebrow-denim cursor-pointer text-left hover:opacity-80 transition-opacity"
                      data-testid={`service-group-label-${slug}`}
                      aria-label={`Start a ${group.label} conversation`}
                    >
                      {group.label}
                    </button>
                    {/* Mobile label: opens the drawer. */}
                    <button
                      type="button"
                      onClick={() =>
                        setOpenServices((prev) => {
                          const next = new Set(prev);
                          if (next.has(slug)) next.delete(slug);
                          else next.add(slug);
                          return next;
                        })
                      }
                      className="md:hidden flex items-center justify-between w-full py-1"
                      aria-expanded={isOpen}
                      data-testid={`service-group-toggle-${slug}`}
                    >
                      <span className="eyebrow eyebrow-denim">{group.label}</span>
                      <Plus
                        className={`w-4 h-4 text-denim-bnm transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}
                        aria-hidden
                      />
                    </button>
                  </div>
                  <div
                    className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] min-w-0 flex-1 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"} md:!grid-rows-[1fr]`}
                  >
                    <div className="overflow-hidden min-h-0">
                      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1.5 pt-2 md:pt-0 pb-1 md:pb-0">
                        {group.items.map((s, si) => (
                          <React.Fragment key={s}>
                            <button
                              type="button"
                              onClick={() => startConversation(serviceSentence(s))}
                              className="text-[14.5px] md:text-[15px] text-ink cursor-pointer transition-colors hover:text-denim-bnm"
                              data-testid={`service-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                              aria-label={`Start a conversation about ${s}`}
                            >
                              {s}
                            </button>
                            {si < group.items.length - 1 && (
                              <span className="text-muted-ink/45 text-[12px]" aria-hidden>•</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const VisionCta = ({ startConversation }) => (
  <section className="border-t border-bnm">
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
      <Reveal as="h2" className="type-h2">
        Your <span className="font-serif-italic font-light">vision</span>
        <br />
        starts <span className="font-serif-italic bnm-denim-shine font-light">here</span>.
      </Reveal>
      <Reveal delay={100}>
        <p className="text-muted-ink text-[16px] md:text-[18px] mt-6 leading-[1.7] max-w-[640px] mx-auto">
          Tell us the vision. We{"\u2019"}ll take care of everything else.
        </p>
      </Reveal>
      <Reveal delay={200}>
        <div className="mt-8 flex flex-col items-center justify-center">
          <button
            type="button"
            onClick={() => startConversation()}
            className="btn-denim w-full sm:w-auto"
            data-testid="connect-start"
          >
            <span>Let{"\u2019"}s Begin</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-5 text-muted-ink text-[14px]" data-testid="connect-call-line">
            Prefer speaking instead?{" "}
            <a href="tel:+919258651242" className="link-thread text-ink" data-testid="connect-call-link">
              Call your Producer.
            </a>
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const MANIFESTO_LINES = [
  { lead: "Every idea", rest: "deserves attention." },
  { lead: "Every brand", rest: "deserves a story." },
  { lead: "Every story", rest: "deserves exceptional craft." },
  { lead: "Every production", rest: "deserves a Producer." },
  { lead: "Every partnership", rest: "deserves trust." },
];

const Manifesto = () => (
  <section className="bg-[#0a0a0a]" data-testid="home-manifesto">
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28">
      <Reveal className="eyebrow eyebrow-soft text-white/40 mb-10 md:mb-14">
        What we believe
      </Reveal>
      <div className="flex flex-col gap-6 md:gap-9">
        {MANIFESTO_LINES.map((line, i) => (
          <Reveal key={line.rest} delay={i * 100} data-testid={`manifesto-line-${i + 1}`}>
            <p className="font-display font-extrabold tracking-[-0.035em] leading-[1.12] text-[26px] sm:text-[34px] md:text-[44px]">
              <span className="text-white/35">{line.lead} </span>
              <span className="text-white">{line.rest}</span>
            </p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={200}>
        <div className="mt-12 md:mt-16 max-w-[520px]">
          <p className="text-white/70 text-[16px] md:text-[17.5px] leading-[1.8]" data-testid="manifesto-closing">
            Great work creates lasting value.
            <br />
            That{"\u2019"}s the standard we hold ourselves to.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

// All homepage marketing sections, in journey order:
// feel the craft -> understand the process -> philosophy -> services -> CTA -> beliefs.
export const HomeMarketing = ({ startConversation }) => (
  <>
    <CraftFilm />
    <ProcessTimeline />
    <Philosophy />
    <ServicesRows startConversation={startConversation} />
    <VisionCta startConversation={startConversation} />
    <Manifesto />
  </>
);

export default HomeMarketing;
